import { QuantityItemProps } from '../QuantityItem.types';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { FocusGroups, PRODUCT_FOCUS_ORDER_STEP } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { productContentFooterTestID } from 'src/helpers/testIDs.helper';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { icon, text } from 'src/theme/colors';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { styles } from './QuantityItemMobile.styles';
import { View } from 'react-native';
import { PressableHovered } from 'src/editor-v3/components/pressable-hovered/PressableHovered';
import { useQuantityItemLogic } from '../QuantityItem.logic';

export const QuantityItemMobile = observer((props: QuantityItemProps) => {
  const { i18n } = useStore();
  const { containerStyle, product, isBackgroundOnDark, isWithText, index = 0 } = props;
  const focusOrderBase = index * PRODUCT_FOCUS_ORDER_STEP;
  const { productCount, addProduct, subtractProduct, removeProduct } = useQuantityItemLogic(product);

  return (
    <View style={containerStyle}>
      <View style={{ alignItems: 'center' }}>
        <View
          style={[styles.buttonsContainer, isBackgroundOnDark ? styles.backgroundOnDark : styles.backgroundOnLight]}
        >
          <PressableHovered
            testID={
              productCount === 1
                ? productContentFooterTestID(product.id, TestIDs.REMOVE)
                : productContentFooterTestID(product.id, TestIDs.SUBTRACT)
            }
            onPress={() => {
              if (productCount === 1) removeProduct({ sectionName: SECTION_NAMES.PICTURE_VIEW });
              else subtractProduct();
            }}
            style={styles.icon}
            hint={productCount === 1 ? i18n.t('actions:delete') : i18n.t('actions:decrease')}
            accessibilityLabel={productCount === 1 ? i18n.t('actions:delete') : i18n.t('actions:decrease')}
            accessibilityRole="button"
            focusGroup={FocusGroups.PRODUCT}
            focusOrder={focusOrderBase + 4}
          >
            <View style={styles.iconAction}>
              <Icon name={productCount === 1 ? ICON_NAMES.Delete : ICON_NAMES.Minus} color={icon.iconInteractive} />
            </View>
          </PressableHovered>

          <TextLato
            testID={productContentFooterTestID(product.id, TestIDs.PRODUCT_COUNT)}
            style={[styles.countText, { color: text.color.textPrimary }]}
            accessibilityLabel={`${i18n.t('actions:quantity')}: ${productCount}`}
          >
            {productCount}
          </TextLato>

          <PressableHovered
            testID={productContentFooterTestID(product.id, TestIDs.INCREASE)}
            onPress={addProduct}
            style={styles.icon}
            disabled={productCount === 9999}
            hint={i18n.t('actions:increase')}
            accessibilityLabel={i18n.t('actions:increase')}
            accessibilityRole="button"
            focusGroup={FocusGroups.PRODUCT}
            focusOrder={focusOrderBase + 6}
          >
            <View style={styles.iconAction}>
              <Icon
                name={ICON_NAMES.Plus}
                color={productCount === 9999 ? icon.iconColorDisabled : icon.iconInteractive}
              />
            </View>
          </PressableHovered>
        </View>
        {isWithText && <TextLato style={styles.buttonText}>{i18n.t('actions:quantity')}</TextLato>}
      </View>
    </View>
  );
});


QuantityItemMobile.displayName = 'QuantityItemMobile';